import { JSONUIProvider, Renderer, defineRegistry } from '@json-render/react';
import { FC } from 'react';

import { type Spec, eluveCatalog } from '@eluve/sdui-types';

import { AlertBannerRenderer } from './AlertBannerRenderer';
import { AlertRenderer } from './AlertRenderer';
import { BoxRenderer } from './BoxRenderer';
import { ButtonRenderer } from './ButtonRenderer';
import { CardRenderer } from './CardRenderer';
import { ChartRenderer } from './ChartRenderer';
import { DividerRenderer } from './DividerRenderer';
import { DynamicArtifactComponentRenderer } from './DynamicArtifactComponentRenderer';
import { ExpandableRenderer } from './ExpandableRenderer';
import { IconRenderer } from './IconRenderer';
import { ImageRenderer } from './ImageRenderer';
import { InstructionStepRenderer } from './InstructionStepRenderer';
import { LinkRenderer } from './LinkRenderer';
import { ProgressRenderer } from './ProgressRenderer';
import { RegistryProvider } from './RegistryContext';
import { TextRenderer } from './TextRenderer';

export const { registry } = defineRegistry(eluveCatalog, {
  components: {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    Root: ({ children }) => <>{children}</>,

    Text: ({ props }) => <TextRenderer {...props} />,

    Divider: () => <DividerRenderer />,

    Expandable: ({ props, children }) => (
      <ExpandableRenderer {...props} children={children} />
    ),

    Link: ({ props, children }) => (
      <LinkRenderer {...props} children={children} />
    ),

    DynamicArtifact: ({ props }) => (
      <DynamicArtifactComponentRenderer {...props} />
    ),

    Alert: ({ props }) => <AlertRenderer {...props} />,

    Icon: ({ props }) => <IconRenderer {...props} />,

    Button: ({ props, emit }) => (
      <ButtonRenderer {...props} onPress={() => emit('press')} />
    ),

    Image: ({ props }) => <ImageRenderer {...props} />,

    InstructionStep: ({ props }) => <InstructionStepRenderer {...props} />,

    Box: ({ props, children }) => (
      <BoxRenderer {...props}>{children}</BoxRenderer>
    ),

    Card: ({ props, children }) => (
      <CardRenderer {...props}>{children}</CardRenderer>
    ),

    AlertBanner: ({ props, children }) => (
      <AlertBannerRenderer variant={props.variant}>
        {children}
      </AlertBannerRenderer>
    ),

    Progress: ({ props }) => <ProgressRenderer {...props} />,

    Chart: ({ props }) => <ChartRenderer {...props} />,
  },
});

/**
 * json-render based renderer for Eluve SDUI components.
 *
 * Usage:
 *   <EluveRenderer spec={spec} />
 *
 * The spec is a json-render Spec with Eluve component types
 * (Text, Divider, Expandable, Link, DynamicArtifact, Alert).
 *
 * Wrap with SDUIAnalyticsProvider and SDUIFeedbackProvider for
 * analytics tracking and feedback UI injection.
 */
export const EluveRenderer: FC<{
  spec: Spec | null;
  loading?: boolean;
}> = ({ spec, loading }) => (
  <JSONUIProvider registry={registry} initialState={spec?.state}>
    <RegistryProvider registry={registry}>
      <Renderer spec={spec} registry={registry} loading={loading} />
    </RegistryProvider>
  </JSONUIProvider>
);
